import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import TableLoader from '../../Components/Spinner/TableLoader';
import TableNoDataMessage from '../../Components/TableMessage/TableNoDataMessage';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { Iuser } from '../../Context/AuthContext/AuthTyps';
import { IDuplicateCertificate } from '../../rest/types';
import { useDuplicateCertificateQuery } from '../../rest/useDuplicateCertificate';
import { getStatusLabel } from '../../Utils/Label/status-label';
import CreateDuplicateApplication from './CreateDuplicateApplication';

interface Data {
  duplicate_certificate_application_id: number;
  duplicate_certificate_application_status: string;
  duplicate_certificate_application_created_at: string;
  action?: string;
}

function createData(
  duplicate_certificate_application_id: number,
  duplicate_certificate_application_status: string,
  duplicate_certificate_application_created_at: string,
): Partial<IDuplicateCertificate> {
  return {
    duplicate_certificate_application_id,
    duplicate_certificate_application_status,
    duplicate_certificate_application_created_at,
  };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'duplicate_certificate_application_id',
    numeric: false,
    disablePadding: false,
    label: 'Application ID',
  },
  {
    id: 'duplicate_certificate_application_status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'duplicate_certificate_application_created_at',
    numeric: false,
    disablePadding: false,
    label: 'Created at',
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
  },
];

function EnhancedTableHead() {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
          >
            <TableSortLabel>{headCell.label}</TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar({
  setOpen,
  user,
}: {
  setOpen: any;
  user: Iuser;
}) {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: '1 1 100%', p: 2 }}
        variant='h6'
        id='tableTitle'
        component='div'
      >
        Duplicate certificate application
      </Typography>

      {user?.user_member_account_status === 'active' && (
        <Tooltip title='Create duplicate certificate application'>
          <Button
            sx={{ width: '250px' }}
            onClick={() => setOpen(true)}
            size='small'
            variant='outlined'
          >
            Certificate application
          </Button>
        </Tooltip>
      )}
    </Toolbar>
  );
}

export default function DuplicateCertificateApplication() {
  const { user } = useAuthContext();
  const { data, fetchData, loading } = useDuplicateCertificateQuery();
  const [open, setOpen] = React.useState<boolean>(false);
  const [reload, setReload] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  useEffect(() => {
    if (user.user_member_id) {
      fetchData({
        duplicate_certificate_application_member_id: user.user_member_id,
      });
    }
  }, [reload, user.user_member_id, fetchData]);

  const rows = data.map((row) =>
    createData(
      row.duplicate_certificate_application_id,
      row.duplicate_certificate_application_status,
      row.duplicate_certificate_application_created_at,
    ),
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <>
      <Box sx={{ width: '100%', mt: 5 }}>
        <Paper sx={{ width: '100%', mb: 2, mt: 5 }}>
          <EnhancedTableToolbar setOpen={setOpen} user={user} />

          <TableContainer>
            <Table aria-labelledby='tableTitle' size={'small'}>
              <EnhancedTableHead />
              <TableBody>
                {loading ? (
                  <TableRow
                    style={{
                      height: 53 * (emptyRows || 1),
                    }}
                  >
                    <TableCell align='center' colSpan={4}>
                      <TableLoader />
                    </TableCell>
                  </TableRow>
                ) : rows.length > 0 ? (
                  rows.map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.duplicate_certificate_application_id || index}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell id={labelId} align='left'>
                          {row.duplicate_certificate_application_id}
                        </TableCell>
                        <TableCell align='left'>
                          {getStatusLabel(
                            row.duplicate_certificate_application_status!,
                          )}
                        </TableCell>
                        <TableCell align='left'>
                          {moment(
                            `${row.duplicate_certificate_application_created_at}`,
                          ).format('DD-MMM-YYYY')}
                        </TableCell>
                        <TableCell align='left'>
                          {row.duplicate_certificate_application_status?.toLowerCase() ===
                          'draft' ? (
                            <Tooltip title='View'>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpen(true);
                                }}
                              >
                                <VisibilityIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title='View'>
                              <Link
                                to={`/duplicate-certificate-application/${row.duplicate_certificate_application_id}`}
                              >
                                <IconButton>
                                  <VisibilityIcon fontSize='small' />
                                </IconButton>
                              </Link>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableNoDataMessage />
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
      <ModalsWrapper
        modalData={{ title: 'Create duplicate certificate application' }}
        setShowModal={setOpen}
        showModal={open}
      >
        <CreateDuplicateApplication
          setReload={() => setReload((prev) => !prev)}
          handelClose={setOpen}
        />
      </ModalsWrapper>
    </>
  );
}
